// Define your domain errors with parameters here
export type DomainErrorCatalog = {
  "MATERIAL_LAYOUT.LENGTH_MUST_BE_LONGER_THAN_SKELETON": {
    minWidth: number;
  };
  "MATERIAL_LAYOUT.PART_LENGHT_MUST_BE_SHORTER_THAN_RAW_MATERIAL_LENGTH": null;
  "MATERIAL_LAYOUT.PART_WIDTH_MUST_BE_SHORTER_THAN_RAW_MATERIAL_WIDTH": null;
  "MATERIAL_LAYOUT.PART_HEIGHT_MUST_BE_SHORTER_THAN_RAW_MATERIAL_HEIGHT": null;
  "MATERIAL_LAYOUT.PART_DIMENSIONS_NEGATIVE": null;
  "MATERIAL_LAYOUT.SHEET_METAL_DIMENSIONS_NEGATIVE": null;
  "MATERIAL_LAYOUT.CUT_WIDTH_NEGATIVE": null;
  "MATERIAL_LAYOUT.SKELETON_WIDTH_NEGATIVE": null;
  "MATERIAL_LAYOUT.RAW_MATERIAL_DIMENSIONS_NEGATIVE": null;
  "MATERIAL_LAYOUT.SHEET_METAL_TOO_SMALL": null;
  "NESTING.MISSING_SHEET_FORMAT": null;
  "NESTING.NO_FLAT_PATTERN": null;
  "NESTING.MISSING_QUOTE_REQUEST_LINE": null;
  "NESTING.NO_RESULT_TO_APPLY": null;
  "EXPORT.VALIDATION_FAILED": {
    errors: Array<{
      type: "required_missing" | "mapping_missing" | "invalid_expression" | "invalid_format";
      message: string;
      context?: Record<string, unknown>;
    }>;
    warnings: Array<{
      type: "truncated" | "empty_section" | "format_conversion";
      message: string;
      context?: Record<string, unknown>;
    }>;
  };
};

export type DomainErrorKey = keyof DomainErrorCatalog;
export type ParamsOf<K extends DomainErrorKey> = DomainErrorCatalog[K];

export interface DomainErrorDTO<K extends DomainErrorKey = DomainErrorKey> {
  code: K;
  params: ParamsOf<K>;
  http: number;
  details?: unknown;
}

export class DomainError<K extends DomainErrorKey = DomainErrorKey> extends Error {
  readonly code: K;
  readonly params: ParamsOf<K>;
  readonly http: number;
  readonly details?: unknown;

  constructor(code: K, params: ParamsOf<K>, options?: { http?: number; details?: unknown }) {
    super(String(code));
    this.name = "DomainError";
    this.code = code;
    this.params = params;
    this.http = options?.http ?? 500;
    this.details = options?.details;

    // Fix prototype chain for extending Error in TypeScript
    Object.setPrototypeOf(this, DomainError.prototype);

    Error.captureStackTrace(this, DomainError);
  }

  toJson(): DomainErrorDTO<K> {
    return {
      code: this.code,
      params: this.params,
      http: this.http,
      details: this.details,
    };
  }

  static fromJson<T extends DomainErrorKey>(data: DomainErrorDTO<T>): DomainError<T> {
    return new DomainError<T>(data.code, data.params, {
      http: data.http,
      details: data.details,
    });
  }
}

export function isDomainError(e: unknown): e is DomainError {
  if (!e || typeof e !== "object") {
    return false;
  }
  if (!(e instanceof Error)) {
    return false;
  }
  return e instanceof DomainError || e.name === "DomainError";
}
